/**
 * Form Builder Service
 * 
 * Handles custom form creation, management, and submissions
 */

import { safeFrom, safeAuth, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notConfiguredError
} from '@/lib/service-errors';
import { auditContent } from '@/lib/audit-logger';
import { 
  emailService,
  sendFormSubmissionNotification
} from './emailService';
import type { 
  CustomForm, 
  CreateFormData, 
  UpdateFormData,
  FormSubmission,
  FormSubmissionStatus,
  GetFormsOptions,
  GetFormSubmissionsOptions,
  FormField
} from '@/types/forms';

/**
 * Generate a slug from a name
 */
const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Validate form field definitions
 */
const validateFields = (fields: FormField[]): { valid: boolean; error?: string } => {
  if (!Array.isArray(fields) || fields.length === 0) {
    return { valid: false, error: 'Form must have at least one field' };
  }

  for (const field of fields) {
    if (!field.id || !field.type || !field.label || !field.name) {
      return { valid: false, error: 'All fields must have id, type, label, and name' };
    }

    // Validate field types that require options
    if (['select', 'radio', 'checkbox'].includes(field.type) && (!field.options || field.options.length === 0)) {
      return { valid: false, error: `${field.type} fields must have options` };
    }
  }

  return { valid: true };
};

/**
 * Get all forms
 */
export const getForms = async (
  options: GetFormsOptions = {}
): Promise<ServiceResponse<CustomForm[]>> => {
  if (!isSupabaseAvailable()) {
    return successResponse([]);
  }

  try {
    let query = safeFrom('custom_forms')
      .select('*')
      .order('created_at', { ascending: false });

    // Apply filters
    if (options.published !== undefined) {
      query = query.eq('published', options.published) as any;
    }

    // Apply pagination
    if (options.limit) {
      query = query.limit(options.limit) as any;
    }
    if (options.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 50) - 1) as any;
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return successResponse((data || []) as CustomForm[]);
  } catch (error) {
    console.error('[formBuilderService] Error fetching forms:', error);
    return errorResponse(handleServiceError(error, 'formBuilderService.getForms', { options }));
  }
};

/**
 * Check if error indicates table doesn't exist
 */
const isTableNotFoundError = (error: any): boolean => {
  if (!error) return false;
  const errorStr = JSON.stringify(error).toLowerCase();
  const message = error.message?.toLowerCase() || '';
  const code = error.code?.toString() || '';
  
  return (
    code === 'PGRST116' ||
    code === '42P01' ||
    message.includes('404') ||
    message.includes('relation') ||
    message.includes('does not exist') ||
    message.includes('table') && message.includes('not found') ||
    errorStr.includes('404') ||
    errorStr.includes('relation') ||
    errorStr.includes('does not exist')
  );
};

/**
 * Get form by slug (published only, for public access)
 */
export const getFormBySlug = async (
  slug: string
): Promise<ServiceResponse<CustomForm | null>> => {
  if (!isSupabaseAvailable()) {
    return successResponse(null);
  }

  try {
    const { data, error } = await safeFrom('custom_forms')
      .select('*')
      .eq('slug', slug)
      .eq('published', true)
      .maybeSingle();

    if (error) {
      // If table doesn't exist, return null (graceful degradation)
      if (isTableNotFoundError(error)) {
        console.warn('[formBuilderService] custom_forms table does not exist. Please run migration: 20260107000003_create_form_builder.sql');
        return successResponse(null);
      }
      throw error;
    }

    return successResponse(data as CustomForm | null);
  } catch (error) {
    // If table doesn't exist, return null instead of error
    if (isTableNotFoundError(error)) {
      console.warn('[formBuilderService] custom_forms table does not exist. Please run migration: 20260107000003_create_form_builder.sql');
      return successResponse(null);
    }
    console.error('[formBuilderService] Error fetching form by slug:', error);
    return errorResponse(handleServiceError(error, 'formBuilderService.getFormBySlug', { slug }));
  }
};

/**
 * Check if form exists by slug (any status, for duplicate checking)
 */
const checkFormExistsBySlug = async (
  slug: string
): Promise<ServiceResponse<CustomForm | null>> => {
  if (!isSupabaseAvailable()) {
    return successResponse(null);
  }

  try {
    const { data, error } = await safeFrom('custom_forms')
      .select('id, slug')
      .eq('slug', slug)
      .maybeSingle();

    if (error) {
      // If table doesn't exist, return null (no form exists)
      if (isTableNotFoundError(error)) {
        return successResponse(null);
      }
      throw error;
    }

    return successResponse(data as CustomForm | null);
  } catch (error) {
    console.error('[formBuilderService] Error checking form by slug:', error);
    return errorResponse(handleServiceError(error, 'formBuilderService.checkFormExistsBySlug', { slug }));
  }
};

/**
 * Get form by ID
 */
export const getFormById = async (
  id: number
): Promise<ServiceResponse<CustomForm | null>> => {
  if (!isSupabaseAvailable()) {
    return successResponse(null);
  }

  try {
    const { data, error } = await safeFrom('custom_forms')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return successResponse(data as CustomForm | null);
  } catch (error) {
    console.error('[formBuilderService] Error fetching form by ID:', error);
    return errorResponse(handleServiceError(error, 'formBuilderService.getFormById', { id }));
  }
};

/**
 * Create a new form
 */
export const createForm = async (
  data: CreateFormData
): Promise<ServiceResponse<CustomForm>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    // Validate fields
    const validation = validateFields(data.fields);
    if (!validation.valid) {
      return errorResponse(handleServiceError(
        new Error(validation.error || 'Invalid form fields'),
        'formBuilderService.createForm',
        { data }
      ));
    }

    // Generate slug if not provided
    const slug = data.slug || slugify(data.name);

    // Check for duplicate slug (any status)
    const existing = await checkFormExistsBySlug(slug);
    if (existing.success && existing.data) {
      return errorResponse(handleServiceError(
        new Error('A form with this slug already exists'),
        'formBuilderService.createForm',
        { slug }
      ));
    }

    // Prepare form data
    const formData: any = {
      name: data.name.trim(),
      slug: slug.trim(),
      description: data.description?.trim() || null,
      fields: data.fields,
      settings: data.settings || {},
      integration_config: data.integration_config || {},
      published: data.published ?? false,
      created_by: user?.user?.id || null,
      updated_by: user?.user?.id || null,
    };

    const { data: form, error: insertError } = await safeFrom('custom_forms')
      .insert([formData])
      .select()
      .single();

    if (insertError) {
      // Provide more helpful error message for 404 (table doesn't exist)
      if (isTableNotFoundError(insertError)) {
        throw new Error('The custom_forms table does not exist. Please run the migration: 20260107000003_create_form_builder.sql in your Supabase SQL Editor.');
      }
      throw insertError;
    }

    const created = form as CustomForm;

    // Audit log
    await auditContent.create('custom_form', created.id.toString(), {
      name: created.name,
      slug: created.slug,
    });

    return successResponse(created);
  } catch (error) {
    console.error('[formBuilderService] Error creating form:', error);
    return errorResponse(handleServiceError(error, 'formBuilderService.createForm', { data }));
  }
};

/**
 * Update an existing form
 */
export const updateForm = async (
  id: number,
  data: UpdateFormData
): Promise<ServiceResponse<CustomForm>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    // Validate fields if provided
    if (data.fields) {
      const validation = validateFields(data.fields);
      if (!validation.valid) {
        return errorResponse(handleServiceError(
          new Error(validation.error || 'Invalid form fields'),
          'formBuilderService.updateForm',
          { id, data }
        ));
      }
    }

    // Prepare update payload
    const updatePayload: any = {
      updated_by: user?.user?.id || null,
    };

    if (data.name !== undefined) updatePayload.name = data.name.trim();
    if (data.slug !== undefined) updatePayload.slug = data.slug.trim();
    if (data.description !== undefined) updatePayload.description = data.description?.trim() || null;
    if (data.fields !== undefined) updatePayload.fields = data.fields;
    if (data.settings !== undefined) updatePayload.settings = data.settings;
    if (data.integration_config !== undefined) updatePayload.integration_config = data.integration_config;
    if (data.published !== undefined) updatePayload.published = data.published;

    // Check for duplicate slug if slug is being updated
    if (data.slug) {
      const existing = await checkFormExistsBySlug(data.slug);
      if (existing.success && existing.data && existing.data.id !== id) {
        return errorResponse(handleServiceError(
          new Error('A form with this slug already exists'),
          'formBuilderService.updateForm',
          { slug: data.slug }
        ));
      }
    }

    const { data: form, error: updateError } = await safeFrom('custom_forms')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single();

    if (updateError) {
      throw updateError;
    }

    const updated = form as CustomForm;

    // Audit log
    await auditContent.update('custom_form', id.toString(), {}, {
      name: updated.name,
      slug: updated.slug,
      published: updated.published,
    });

    return successResponse(updated);
  } catch (error) {
    console.error('[formBuilderService] Error updating form:', error);
    return errorResponse(handleServiceError(error, 'formBuilderService.updateForm', { id, data }));
  }
};

/**
 * Delete a form
 */
export const deleteForm = async (
  id: number
): Promise<ServiceResponse<void>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    const { error } = await safeFrom('custom_forms')
      .delete()
      .eq('id', id);

    if (error) {
      throw error;
    }

    // Audit log
    await auditContent.delete('custom_form', id.toString());

    return successResponse(undefined);
  } catch (error) {
    console.error('[formBuilderService] Error deleting form:', error);
    return errorResponse(handleServiceError(error, 'formBuilderService.deleteForm', { id }));
  }
};

/**
 * Submit a form
 */
export const submitForm = async (
  formId: number,
  data: Record<string, any>
): Promise<ServiceResponse<FormSubmission>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Get form definition
    const formResult = await getFormById(formId);
    if (!formResult.success || !formResult.data) {
      return errorResponse(handleServiceError(
        new Error('Form not found'),
        'formBuilderService.submitForm',
        { formId }
      ));
    }

    const form = formResult.data;

    // Validate submission data against form fields
    for (const field of form.fields) {
      const value = data[field.name];

      // Check required fields
      if (field.required && (!value || (typeof value === 'string' && value.trim().length === 0))) {
        return errorResponse(handleServiceError(
          new Error(`${field.label} is required`),
          'formBuilderService.submitForm',
          { formId, field: field.name }
        ));
      }

      // Validate email format
      if (field.type === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return errorResponse(handleServiceError(
            new Error(`Invalid email address for ${field.label}`),
            'formBuilderService.submitForm',
            { formId, field: field.name }
          ));
        }
      }

      // Validate number fields
      if (field.type === 'number' && value) {
        const num = Number(value);
        if (isNaN(num)) {
          return errorResponse(handleServiceError(
            new Error(`${field.label} must be a number`),
            'formBuilderService.submitForm',
            { formId, field: field.name }
          ));
        }
        if (field.validation?.min !== undefined && num < field.validation.min) {
          return errorResponse(handleServiceError(
            new Error(`${field.label} must be at least ${field.validation.min}`),
            'formBuilderService.submitForm',
            { formId, field: field.name }
          ));
        }
        if (field.validation?.max !== undefined && num > field.validation.max) {
          return errorResponse(handleServiceError(
            new Error(`${field.label} must be at most ${field.validation.max}`),
            'formBuilderService.submitForm',
            { formId, field: field.name }
          ));
        }
      }

      // Validate text length
      if (field.type === 'textarea' && value && field.validation?.min) {
        if (value.length < field.validation.min) {
          return errorResponse(handleServiceError(
            new Error(`${field.label} must be at least ${field.validation.min} characters`),
            'formBuilderService.submitForm',
            { formId, field: field.name }
          ));
        }
      }
    }

    // Extract email for submitted_by if available
    const emailField = form.fields.find(f => f.type === 'email');
    const submittedBy = emailField ? data[emailField.name] : null;

    // Create submission
    const { data: submission, error: insertError } = await safeFrom('form_submissions')
      .insert([{
        form_id: formId,
        data: data,
        submitted_by: submittedBy || null,
        status: 'new' as FormSubmissionStatus,
      }])
      .select()
      .single();

    if (insertError) {
      throw insertError;
    }

    const created = submission as FormSubmission;

    // Execute integration actions (non-blocking)
    if (form.integration_config.email?.enabled) {
      const emailConfig = form.integration_config.email;
      const recipients = emailConfig.to || [import.meta.env.VITE_ADMIN_EMAIL || 'admin@example.com'];
      
      // Replace template variables in subject
      let subject = emailConfig.subject || `New Submission: ${form.name}`;
      Object.entries(data).forEach(([key, value]) => {
        subject = subject.replace(new RegExp(`{{${key}}}`, 'g'), String(value));
      });

      sendFormSubmissionNotification(
        form.name,
        data,
        recipients[0],
        {
          emailSubject: subject,
          emailTemplate: emailConfig.customTemplate,
        }
      ).catch(error => {
        console.error('[formBuilderService] Failed to send notification email:', error);
      });

      // Send auto-reply if enabled
      if (emailConfig.autoReply?.enabled && submittedBy) {
        let replyMessage = emailConfig.autoReply.message || 'Thank you for your submission!';
        Object.entries(data).forEach(([key, value]) => {
          replyMessage = replyMessage.replace(new RegExp(`{{${key}}}`, 'g'), String(value));
        });

        emailService.sendEmail({
          to: submittedBy,
          subject: emailConfig.autoReply.subject || `Thank you for your submission to ${form.name}`,
          html: `<p>${replyMessage.replace(/\n/g, '<br>')}</p>`,
        }).catch(error => {
          console.error('[formBuilderService] Failed to send auto-reply email:', error);
        });
      }
    }

    // Execute webhook if configured (non-blocking)
    if (form.integration_config.webhook?.enabled && form.integration_config.webhook.url) {
      fetch(form.integration_config.webhook.url, {
        method: form.integration_config.webhook.method || 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...form.integration_config.webhook.headers,
        },
        body: JSON.stringify({
          form_id: formId,
          form_name: form.name,
          data: data,
          submitted_at: created.created_at,
        }),
      }).catch(error => {
        console.error('[formBuilderService] Failed to send webhook:', error);
      });
    }

    return successResponse(created);
  } catch (error) {
    console.error('[formBuilderService] Error submitting form:', error);
    return errorResponse(handleServiceError(error, 'formBuilderService.submitForm', { formId, data }));
  }
};

/**
 * Get form submissions
 */
export const getFormSubmissions = async (
  options: GetFormSubmissionsOptions = {}
): Promise<ServiceResponse<FormSubmission[]>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    let query = safeFrom('form_submissions')
      .select('*')
      .order('created_at', { ascending: false });

    // Apply filters
    if (options.formId) {
      query = query.eq('form_id', options.formId) as any;
    }
    if (options.status) {
      query = query.eq('status', options.status) as any;
    }

    // Apply pagination
    if (options.limit) {
      query = query.limit(options.limit) as any;
    }
    if (options.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 50) - 1) as any;
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return successResponse((data || []) as FormSubmission[]);
  } catch (error) {
    console.error('[formBuilderService] Error fetching form submissions:', error);
    return errorResponse(handleServiceError(error, 'formBuilderService.getFormSubmissions', { options }));
  }
};

/**
 * Update submission status
 */
export const updateSubmissionStatus = async (
  id: number,
  status: FormSubmissionStatus
): Promise<ServiceResponse<FormSubmission>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    const { data: submission, error: updateError } = await safeFrom('form_submissions')
      .update({ status })
      .eq('id', id)
      .select()
      .single();

    if (updateError) {
      throw updateError;
    }

    return successResponse(submission as FormSubmission);
  } catch (error) {
    console.error('[formBuilderService] Error updating submission status:', error);
    return errorResponse(handleServiceError(error, 'formBuilderService.updateSubmissionStatus', { id, status }));
  }
};

export const formBuilderService = {
  getForms,
  getFormBySlug,
  getFormById,
  createForm,
  updateForm,
  deleteForm,
  submitForm,
  getFormSubmissions,
  updateSubmissionStatus,
};

